import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { contentHasFileLinkToken } from '../../../../models/utils/richContentTokenUtils';
import { AutomationStepCardTextingInput } from '../../../components/automation/AutomationStepCard/presentation';
import { styleSheet as cardStyleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { TextingStepAttachments } from '../../../components/automation/TextingStepAttachments';
import { InputFieldError } from '../../../components/InputFieldError';
import { bs } from '../../../styles/styles';

interface TextingStepFormProps {
	step: Api.ITextingAutomationStep | null;
	trigger: Api.IAutomationTrigger | null;
	schedule: Api.IAutomationStepSchedule | null;
	readonly: boolean;
	showRequiredFieldError: boolean;
	/** Template ID for conditional step mutations and attachment API calls */
	templateId?: string;
	/** Step ID for conditional step mutations and attachment API calls */
	stepId?: string;
	/** Impersonation context for admin users editing customer automations */
	impersonationContext?: Api.IImpersonationContext;
	canSendFileLinks: boolean;
	canRequestFiles: boolean;
	onAttachmentStepChanged: (updatedStep: Api.ITextingAutomationStep) => void;
	onSaveStep: (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => Promise<void>;
	onClearError: () => void;
	/** Callback when a conditional step is added/removed, to refresh template data */
	onStepChanged?: () => void;
	/** Callback to update the step data locally after a mutation (e.g. conditional step added/removed) */
	updateStep?: (stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => void;
}

/**
 * Form component for editing texting automation step content.
 * Displays a text editor for composing SMS/text messages with merge fields.
 */
export function TextingStepForm({
	step,
	trigger,
	schedule,
	readonly,
	showRequiredFieldError,
	templateId,
	stepId,
	impersonationContext,
	canSendFileLinks,
	canRequestFiles,
	onAttachmentStepChanged,
	onSaveStep,
	onClearError,
	onStepChanged,
	updateStep,
}: TextingStepFormProps) {
	const insertEditorContentRef = React.useRef<((content: string) => string) | null>(null);
	const removeEditorContentRef = React.useRef<((symbolOrSymbols: string | string[]) => string | undefined) | null>(
		null
	);
	const hasContentTokenRef = React.useRef<(() => boolean) | null>(null);
	const hasTextContent = !!step?.content;

	const errorMessage = React.useMemo(() => {
		if (showRequiredFieldError && !hasTextContent) {
			return 'Please provide text content.';
		}
		return null;
	}, [showRequiredFieldError, hasTextContent]);

	if (!step) {
		return <div>Loading step data...</div>;
	}

	return (
		<>
			<div className={css(cardStyleSheet.fieldLabel)}>
				Text content: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<AutomationStepCardTextingInput
					index={0}
					onRequestClearErrorMessages={onClearError}
					onRequestSave={onSaveStep}
					onStepChanged={onStepChanged}
					updateStep={updateStep}
					schedule={schedule ?? undefined}
					step={step}
					trigger={trigger ?? undefined}
					readOnly={readonly}
					templateId={templateId}
					stepId={stepId}
					impersonationContext={impersonationContext}
					insertContentRef={insertEditorContentRef}
					removeContentRef={removeEditorContentRef}
					hasContentTokenRef={hasContentTokenRef}
					canSendFileLinks={canSendFileLinks}
					canRequestFiles={canRequestFiles}
					renderBeforeFallbackEmail={
						templateId && stepId ? (
							<TextingStepAttachments
								step={step}
								templateId={templateId}
								stepId={stepId}
								readonly={readonly}
								canSendFileLinks={canSendFileLinks}
								contentHasFileLinkToken={contentHasFileLinkToken(step.content)}
								impersonationContext={impersonationContext}
								onStepChanged={onAttachmentStepChanged}
								onHasFileLinkToken={() => hasContentTokenRef.current?.() ?? contentHasFileLinkToken(step.content)}
								onSave={(sendAsLink, newHtml) => {
									const fields: Partial<Api.ITextingAutomationStep> = {
										sendAttachmentsAsLink: sendAsLink || undefined,
									};
									if (newHtml !== undefined) {
										fields.content = Api.createRawRichTextContentStateWithHtmlStringValue(newHtml);
									}
									void onSaveStep(fields);
								}}
								onInsertMergeField={sym => insertEditorContentRef.current?.(sym) ?? undefined}
								onRemoveMergeField={syms => removeEditorContentRef.current?.(syms)}
							/>
						) : undefined
					}
				/>
			</InputFieldError>
		</>
	);
}
